import React, { ReactElement, ReactNode } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { FORM_DIRECTION_TYPE, FormDirectionType, Tooltip } from '@bucketplace/commerce-admin-ui';
import { FONT_STYLE } from '@bucketplace/design-system';

interface FormContentProps {
  header?: string;
  /** field들이 필수 입력임을 나타내는 용도로 사용합니다. 별도의 유효성 검증은 하지 않습니다. */
  required?: boolean;
  /** 입력한 값을 바탕으로 header 옆에 tooltip이 표시됩니다. */
  tooltip?: ReactNode;
  /** header에 대한 direction을 설정합니다. */
  direction?: FormDirectionType;
  children: ReactNode;
  className?: string;
}

export function FormContent({
  header,
  children,
  required,
  tooltip,
  direction = FORM_DIRECTION_TYPE.HORIZONTAL,
  className,
}: FormContentProps): ReactElement {
  return (
    <Container
      direction={direction}
      className={className}
    >
      {
        (header || tooltip) && (
          <Header direction={direction}>
            <div>
              <HeaderTitle required={required}>{ header }</HeaderTitle>
              { tooltip && <Tooltip>{ tooltip }</Tooltip> }
            </div>
          </Header>
        )
      }
      <Content>
        { children }
      </Content>
    </Container>
  );
}

const Container = styled.div<{
  direction?: FormDirectionType,
}>`
  display: flex;
  align-items: stretch;

  ${({ direction }) => direction === FORM_DIRECTION_TYPE.VERTICAL && `
    flex-direction: column;
  `}
`;

const Header = styled.header<{
  direction?: FormDirectionType,
}>`
  padding: 24px 16px;
  width: ${({ direction }) => direction === FORM_DIRECTION_TYPE.VERTICAL ? '100%' : '144px'};
  min-width: 144px;

  > div {
    display: flex;
    align-items: center;
  }

  ${({ direction }) => direction === FORM_DIRECTION_TYPE.VERTICAL && `
    padding: 24px 16px 0;
  `}
`;

const HeaderTitle = styled.span<{ required?: boolean }>`
  ${FONT_STYLE.subTitle2}
  color: ${({ theme }) => theme.content.base2};
  font-weight: 400;
  white-space: pre-wrap;

  &::after {
    ${({ required, theme }) => required && css`
      content: '*';
      display: inline-block;
      color: ${theme.content.primary2.thick};
    `}
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 16px 16px 0;
`;
